/* eslint-disable react-refresh/only-export-components */
import { useCallback, useMemo, useState, useRef, type ReactNode } from 'react';
import { useHeader, useAuth } from '@/contexts';
import {
  GestaoDocumentosContext,
  type TipoDocumento,
  type FiltroState,
  type FiltroBase,
} from '@/contexts/GestaoDocumentosContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/shared/Pagination';
import { SlidersHorizontal } from 'lucide-react';
import type {
  SimplesNacionalDocumento,
  SimplesNacionalGrupo,
} from '@/services/SimplesNacional/GestaoDocumentos/api/ListaDocumentosSimplesApi';
import type { ItemDocumento } from '@/services/Documentos/types';
import type { EventoDocumento } from '@/services/SimplesNacional/GestaoDocumentos/api/ListaEventosSimplesApi';
import type { HistoricoUploadItem } from '@/services/SimplesNacional/GestaoDocumentos/api/HistoricoUploadApi';
import { useListaDocumentosSimples } from '@/services/SimplesNacional/GestaoDocumentos/hooks/useListaDocumentosSimples';
import { useListaDocumentosItensSimples } from '@/services/SimplesNacional/GestaoDocumentos/hooks/useListaDocumentosItensSimples';
import { useListaEventosSimples } from '@/services/SimplesNacional/GestaoDocumentos/hooks/useListaEventosSimples';
import { useHistoricoUpload } from '@/services/SimplesNacional/GestaoDocumentos/hooks/useHistoricoUpload';
import {
  reprocessarXML,
  priorizar,
  cancelarProcessamento,
} from '@/services/SimplesNacional/GestaoDocumentos/api/HistoricoUploadApi';
import { useGerarLinkTemporario } from '@/services/General';
import { useToast } from '@/hooks/use-toast';
import {
  ITENS_POR_PAGINA,
  POR_PAGINA,
  HISTORICO_ITENS_POR_PAGINA,
  BUCKET,
  ACCEPTED_EXTENSIONS,
  MODELO_NOME,
  MODELO_KEY,
} from '@/lib/utils/helpers';
import { fmt, fmtMoeda, buildS3Key, hoje, primeiroDiaMes } from '@/lib/utils/formatters';
export {
  ITENS_POR_PAGINA,
  POR_PAGINA,
  HISTORICO_ITENS_POR_PAGINA,
  BUCKET,
  ACCEPTED_EXTENSIONS,
  MODELO_NOME,
  MODELOS_COM_ITENS,
  MODELOS_COM_EVENTOS,
  MODELO_KEY,
  PERIODOS,
  STATUS_MAP,
} from '@/lib/utils/helpers';
export {
  formatData,
  formatMoeda,
  formatPct,
  formatDataHora,
  formatDataHoraHistorico,
  fmt,
  fmtMoeda,
  sanitizeFilename,
  buildS3Key,
  hoje,
  primeiroDiaMes,
} from '@/lib/utils/formatters';
export { StatusBadge } from './components/StatusBadge';
export { SkeletonRow } from './components/SkeletonRow';
export { InfoRow } from './components/InfoRow';
export { TributoCard } from './components/TributoCard';
export { TabCabecalho } from './components/TabCabecalho';
export { TabTributos } from './components/TabTributos';
export { TabLabel } from './components/TabLabel';
export { EventoCard } from './components/EventoCard';
export { SkeletonCard } from './components/SkeletonCard';
export { HistoricoStatusBadge } from './components/HistoricoStatusBadge';
export { HistoricoProgressBar } from './components/HistoricoProgressBar';
export { HistoricoSkeletonRow } from './components/HistoricoSkeletonRow';

export interface ColDef {
  key: keyof ItemDocumento | string;
  label: string;
  defaultVisible: boolean;
  render: (item: ItemDocumento) => ReactNode;
  className?: string;
}

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

// ─── COLUNAS (JSX — must live in .tsx) ───────────────────────────────────────

export const COLUNAS: ColDef[] = [
  {
    key: 'Sequencial',
    label: 'Seq',
    defaultVisible: true,
    className: 'w-10 text-center',
    render: i => i.Sequencial,
  },
  {
    key: 'Produto',
    label: 'Produto',
    defaultVisible: true,
    render: i => <span className="font-mono text-xs">{i.Produto}</span>,
  },
  {
    key: 'Descricao',
    label: 'Descrição',
    defaultVisible: true,
    render: i => <span className="text-sm">{i.Descricao || '—'}</span>,
  },
  {
    key: 'Qtde',
    label: 'Qtde',
    defaultVisible: true,
    className: 'text-right',
    render: i => `${fmt(i.Qtde, 4)} ${i.Unidade || ''}`.trim(),
  },
  {
    key: 'ValTot',
    label: 'Valor Total',
    defaultVisible: true,
    className: 'text-right',
    render: i => fmtMoeda(i.ValTot),
  },
  { key: 'CFOP', label: 'CFOP', defaultVisible: true, render: i => i.CFOP || '—' },
  { key: 'NCM', label: 'NCM', defaultVisible: false, render: i => i.NCM || '—' },
  { key: 'EAN', label: 'EAN', defaultVisible: false, render: i => i.EAN || '—' },
  { key: 'CST', label: 'CST ICMS', defaultVisible: true, render: i => i.CST?.trim() || '—' },
  {
    key: 'ICMS',
    label: 'Alíq. ICMS',
    defaultVisible: true,
    className: 'text-right',
    render: i => `${fmt(i.ICMS)}%`,
  },
  {
    key: 'ValorBaseICMS',
    label: 'BC ICMS',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.ValorBaseICMS),
  },
  {
    key: 'ValorICMS',
    label: 'Vlr. ICMS',
    defaultVisible: true,
    className: 'text-right',
    render: i => fmtMoeda(i.ValorICMS),
  },
  {
    key: 'ICMSST',
    label: 'Alíq. ST',
    defaultVisible: false,
    className: 'text-right',
    render: i => `${fmt(i.ICMSST)}%`,
  },
  {
    key: 'ValorICMSST',
    label: 'Vlr. ICMS ST',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.ValorICMSST),
  },
  { key: 'CSTPis', label: 'CST PIS', defaultVisible: false, render: i => i.CSTPis || '—' },
  {
    key: 'PISAliq',
    label: 'Alíq. PIS',
    defaultVisible: false,
    className: 'text-right',
    render: i => `${fmt(i.PISAliq)}%`,
  },
  {
    key: 'VL_BC_PIS',
    label: 'BC PIS',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.VL_BC_PIS),
  },
  {
    key: 'ValorPIS',
    label: 'Vlr. PIS',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.ValorPIS),
  },
  { key: 'CSTCofins', label: 'CST COFINS', defaultVisible: false, render: i => i.CSTCofins || '—' },
  {
    key: 'COFINSAliq',
    label: 'Alíq. COFINS',
    defaultVisible: false,
    className: 'text-right',
    render: i => `${fmt(i.COFINSAliq)}%`,
  },
  {
    key: 'VL_BC_COFINS',
    label: 'BC COFINS',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.VL_BC_COFINS),
  },
  {
    key: 'ValorCOFINS',
    label: 'Vlr. COFINS',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.ValorCOFINS),
  },
  { key: 'CSTIpi', label: 'CST IPI', defaultVisible: false, render: i => i.CSTIpi || '—' },
  {
    key: 'IPIAliq',
    label: 'Alíq. IPI',
    defaultVisible: false,
    className: 'text-right',
    render: i => `${fmt(i.IPIAliq)}%`,
  },
  {
    key: 'ValorIPI',
    label: 'Vlr. IPI',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.ValorIPI),
  },
  {
    key: 'cClassTrib',
    label: 'Class. Trib.',
    defaultVisible: false,
    render: i => i.cClassTrib?.trim() || '—',
  },
  {
    key: 'vIBS',
    label: 'Vlr. IBS',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.vIBS),
  },
  {
    key: 'vCBS',
    label: 'Vlr. CBS',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.vCBS),
  },
  {
    key: 'vFrete',
    label: 'Frete',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.vFrete),
  },
  {
    key: 'ValDesc',
    label: 'Desconto',
    defaultVisible: false,
    className: 'text-right',
    render: i => fmtMoeda(i.ValDesc),
  },
];

// ─── Custom hooks ─────────────────────────────────────────────────────────────

export function useGrupoDocumentos(grupo: SimplesNacionalGrupo, filtroBase: FiltroBase) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPaginas =
    grupo.data.length < ITENS_POR_PAGINA
      ? 1
      : Math.max(1, Math.ceil(grupo.totalItens / ITENS_POR_PAGINA));
  const nomeModelo = MODELO_NOME[grupo.modelo] ?? grupo.modelo;

  const needsPageFetch = isOpen && currentPage > 1;
  const { data: pageData, isLoading: isPageLoading } = useListaDocumentosSimples(
    {
      ...filtroBase,
      codigoModelo: grupo.codigoModelo,
      pagina_numero: currentPage,
      pagina_quantidade_por: ITENS_POR_PAGINA,
    },
    needsPageFetch
  );

  const documentos: SimplesNacionalDocumento[] =
    currentPage === 1 ? grupo.data : (pageData?.data?.[0]?.data ?? []);

  function handlePageChange(page: number) {
    setCurrentPage(page);
  }

  return {
    isOpen,
    setIsOpen,
    currentPage,
    handlePageChange,
    documentos,
    isPageLoading,
    totalPaginas,
    nomeModelo,
  };
}

export function useGestaoDocumentosTable() {
  const [selectedDoc, setSelectedDoc] = useState<SimplesNacionalDocumento | null>(null);
  return { selectedDoc, setSelectedDoc };
}

interface UseTabItensProps {
  chaveAcesso: string;
  codigoModelo: string;
  cnpjContribuinte: string;
  dataInicio: string;
  dataFim: string;
  tipo: 'entradas' | 'saidas';
}

export function useTabItens({
  chaveAcesso,
  codigoModelo,
  cnpjContribuinte,
  dataInicio,
  dataFim,
}: UseTabItensProps) {
  const { user } = useAuth();
  const cnpjGestor = user?.cnpj ?? '';

  const [pagina, setPagina] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(5);
  const [filtroCFOP, setFiltroCFOP] = useState('');
  const [filtroClassTrib, setFiltroClassTrib] = useState('');
  const [colsVisiveis, setColsVisiveis] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(COLUNAS.map(c => [c.key, c.defaultVisible]))
  );

  const modeloKey = MODELO_KEY[codigoModelo];

  const { data, isLoading } = useListaDocumentosItensSimples(
    {
      ModeloDocumento: codigoModelo,
      ChaveAcesso: chaveAcesso,
      CNPJ_Gestor: cnpjGestor,
      CNPJ_Contribuinte: cnpjContribuinte,
      DataInicio: dataInicio,
      DataFim: dataFim,
      CFOP: filtroCFOP,
      CClasTrib: filtroClassTrib,
      Pagina: pagina,
      QtdPorPagina: itensPorPagina,
    },
    !!chaveAcesso && !!cnpjGestor
  );

  const grupo = modeloKey ? data?.documents?.[modeloKey] : undefined;
  const itens: ItemDocumento[] = grupo?.items ?? [];
  const totalItens = grupo?.totalItens ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(totalItens / itensPorPagina));

  const colsAtivas = useMemo(
    () => COLUNAS.filter(c => colsVisiveis[c.key as string]),
    [colsVisiveis]
  );

  function toggleCol(key: string) {
    setColsVisiveis(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function handleFiltroChange() {
    setPagina(1);
  }

  function handleItensPorPaginaChange(n: number) {
    setItensPorPagina(n);
    setPagina(1);
  }

  return {
    pagina,
    setPagina,
    itensPorPagina,
    filtroCFOP,
    setFiltroCFOP,
    filtroClassTrib,
    setFiltroClassTrib,
    colsVisiveis,
    toggleCol,
    colsAtivas,
    isLoading,
    itens,
    totalItens,
    totalPaginas,
    handleFiltroChange,
    handleItensPorPaginaChange,
  };
}

export function useTabEventos(chaveAcesso: string, codigoModelo: string) {
  const [pagina, setPagina] = useState(1);

  const { data, isLoading } = useListaEventosSimples(
    { TipoDocumento: codigoModelo, ChaveAcessoDOC: chaveAcesso },
    !!chaveAcesso
  );

  const todos: EventoDocumento[] = data?.data ?? [];
  const totalPaginas = Math.max(1, Math.ceil(todos.length / POR_PAGINA));
  const inicio = (pagina - 1) * POR_PAGINA;
  const pagAtual = todos.slice(inicio, inicio + POR_PAGINA);

  return { pagina, setPagina, todos, isLoading, totalPaginas, pagAtual };
}

export function useUploadXml(cnpj: string) {
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const gerarLink = useGerarLinkTemporario();

  function reset() {
    setFile(null);
    setStatus('idle');
    setErrorMessage('');
  }

  function handleClose(open: boolean) {
    if (!open) reset();
  }

  function validateAndSet(selected: File) {
    const ext = selected.name.slice(selected.name.lastIndexOf('.')).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setErrorMessage('Apenas arquivos .zip ou .rar são aceitos.');
      setFile(null);
      return;
    }
    setErrorMessage('');
    setFile(selected);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0];
    if (selected) validateAndSet(selected);
    e.target.value = '';
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) validateAndSet(dropped);
  }

  async function handleUpload() {
    if (!file) return;
    setStatus('uploading');
    setErrorMessage('');
    try {
      const key = buildS3Key(cnpj, file.name);
      const linkResponse = await gerarLink.mutateAsync({ Funcao: 'PUT', Bucket: BUCKET, Key: key });
      if (!linkResponse.status?.resultado) {
        throw new Error(
          linkResponse.status?.mensagem || 'Não foi possível gerar o link de upload.'
        );
      }
      const uploadUrl =
        typeof linkResponse.data === 'string' ? linkResponse.data : linkResponse.data?.url;
      if (!uploadUrl) throw new Error('URL de upload não retornada pela API.');
      const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      const contentType =
        ext === '.rar' ? 'application/x-compressed; charset=UTF-8' : 'application/x-zip-compressed';
      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType },
      });
      if (!putResponse.ok) throw new Error(`Erro ao enviar arquivo: ${putResponse.status}`);
      setStatus('success');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Erro inesperado ao fazer upload.');
      setStatus('error');
    }
  }

  return {
    file,
    status,
    errorMessage,
    dragOver,
    setDragOver,
    inputRef,
    reset,
    handleClose,
    validateAndSet,
    handleFileChange,
    handleDrop,
    handleUpload,
    isUploading: status === 'uploading',
  };
}

export function useHistoricoProcessamentos(cnpj: string) {
  const [periodo, setPeriodo] = useState(15);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [reprocessandoId, setReprocessandoId] = useState<string | null>(null);
  const [priorizandoId, setPriorizandoId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [cancelandoId, setCancelandoId] = useState<string | null>(null);
  const { toast } = useToast();

  const { data, isLoading, isFetching, refetch, isError } = useHistoricoUpload(
    periodo,
    cnpj,
    !!cnpj
  );

  const todosDocumentos: HistoricoUploadItem[] = data?.data?.documentos ?? [];
  const totalPaginas = Math.max(1, Math.ceil(todosDocumentos.length / HISTORICO_ITENS_POR_PAGINA));
  const inicio = (paginaAtual - 1) * HISTORICO_ITENS_POR_PAGINA;
  const documentos = todosDocumentos.slice(inicio, inicio + HISTORICO_ITENS_POR_PAGINA);

  function handlePeriodoChange(v: string) {
    setPeriodo(Number(v));
    setPaginaAtual(1);
  }

  async function handleDownload(item: HistoricoUploadItem) {
    if (!item.CaminhoArquivo) return;
    setDownloadingId(item.ID_Upload);
    try {
      const slashIdx = item.CaminhoArquivo.indexOf('/');
      const bucket = item.CaminhoArquivo.slice(0, slashIdx);
      const key = item.CaminhoArquivo.slice(slashIdx + 1);
      const url = `https://${bucket}.s3.amazonaws.com/${key}`;
      const filename = key.split('/').pop() || item.NomeArquivo || 'download';
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro ao baixar',
        description:
          err instanceof Error ? err.message : 'Não foi possível gerar o link de download.',
      });
    } finally {
      setDownloadingId(null);
    }
  }

  async function handlePriorizar(item: HistoricoUploadItem) {
    setPriorizandoId(item.ID_Upload);
    try {
      await priorizar(item.ID_Upload);
      toast({
        title: 'Upload priorizado',
        description: `${item.NomeArquivo || item.ID_Upload} foi movido para o topo da fila.`,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Erro ao priorizar',
        description: 'Não foi possível priorizar o upload. Tente novamente.',
      });
    } finally {
      setPriorizandoId(null);
    }
  }

  async function handleReprocessar(item: HistoricoUploadItem) {
    setReprocessandoId(item.ID_Upload);
    try {
      await reprocessarXML(item.ID_Upload);
      toast({
        title: 'Reprocessamento iniciado',
        description: `Arquivo ${item.NomeArquivo || item.ID_Upload} enviado para reprocessamento.`,
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Erro ao reprocessar',
        description: 'Não foi possível iniciar o reprocessamento. Tente novamente.',
      });
    } finally {
      setReprocessandoId(null);
    }
  }

  async function handleCancelar(item: HistoricoUploadItem) {
    setCancelandoId(item.ID_Upload);
    try {
      await cancelarProcessamento(item.ID_Upload);
      toast({
        title: 'Processamento cancelado',
        description: `Arquivo ${item.NomeArquivo || item.ID_Upload} foi cancelado.`,
      });
      refetch();
    } catch {
      toast({
        variant: 'destructive',
        title: 'Erro ao cancelar',
        description: 'Não foi possível cancelar o processamento. Tente novamente.',
      });
    } finally {
      setCancelandoId(null);
    }
  }

  return {
    periodo,
    handlePeriodoChange,
    paginaAtual,
    setPaginaAtual,
    reprocessandoId,
    priorizandoId,
    downloadingId,
    data,
    isLoading,
    isFetching,
    refetch,
    isError,
    todosDocumentos,
    totalPaginas,
    documentos,
    inicio,
    handleDownload,
    handlePriorizar,
    handleReprocessar,
    cancelandoId,
    handleCancelar,
  };
}

// ─── Provider ────────────────────────────────────────────────────────────────

const filtroInicial: FiltroState = { modelo: '', status: '' };

export function GestaoDocumentosProvider({ children }: { children: ReactNode }) {
  const { filtros } = useHeader();
  const cnpjSelecionado = filtros.empresaId ?? null;

  const [tabAtiva, setTabAtiva] = useState<TipoDocumento>('entradas');
  const [uploadAberto, setUploadAberto] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [filtrosVisiveis, setFiltrosVisiveis] = useState(false);
  const [filtroPendente, setFiltroPendente] = useState<FiltroState>(filtroInicial);
  const [filtroAplicado, setFiltroAplicado] = useState<FiltroState>(filtroInicial);

  const filtroBase = useMemo(
    () => ({
      entrada_saida: tabAtiva === 'entradas' ? 1 : 0,
      cnpj_contribuinte: cnpjSelecionado ?? '',
      data_emissao_de: filtros.dataInicio ?? primeiroDiaMes(),
      data_emissao_ate: filtros.dataFim ?? hoje(),
      status: filtroAplicado.status,
    }),
    [tabAtiva, filtroAplicado, cnpjSelecionado, filtros.dataInicio, filtros.dataFim]
  );

  const filtroAPIBase = useMemo(
    () => ({
      cnpj_contribuinte: cnpjSelecionado ?? '',
      codigoModelo: filtroAplicado.modelo || '',
      data_emissao_de: filtros.dataInicio ?? primeiroDiaMes(),
      data_emissao_ate: filtros.dataFim ?? hoje(),
      status: filtroAplicado.status,
      pagina_numero: 1,
      pagina_quantidade_por: ITENS_POR_PAGINA,
    }),
    [filtroAplicado, cnpjSelecionado, filtros.dataInicio, filtros.dataFim]
  );

  const apiEnabled = !!cnpjSelecionado;

  const {
    data: dataEntradas,
    isLoading: isLoadingEntradas,
    isError: isErrorEntradas,
  } = useListaDocumentosSimples({ ...filtroAPIBase, entrada_saida: 1 }, apiEnabled);

  const {
    data: dataSaidas,
    isLoading: isLoadingSaidas,
    isError: isErrorSaidas,
  } = useListaDocumentosSimples({ ...filtroAPIBase, entrada_saida: 0 }, apiEnabled);

  const gruposEntradas = dataEntradas?.data ?? [];
  const gruposSaidas = dataSaidas?.data ?? [];

  const grupos = tabAtiva === 'entradas' ? gruposEntradas : gruposSaidas;
  const totalEntradas = gruposEntradas.reduce((sum, g) => sum + g.totalItens, 0);
  const totalSaidas = gruposSaidas.reduce((sum, g) => sum + g.totalItens, 0);
  const isError = isErrorEntradas || isErrorSaidas;

  const aplicarFiltros = useCallback(() => {
    setFiltroAplicado(filtroPendente);
    setFiltrosVisiveis(false);
  }, [filtroPendente]);

  const resetarFiltros = useCallback(() => {
    setFiltroPendente(filtroInicial);
    setFiltroAplicado(filtroInicial);
  }, []);

  const value = useMemo(
    () => ({
      cnpjSelecionado,
      tabAtiva,
      setTabAtiva,
      filtroPendente,
      setFiltroPendente,
      filtroAplicado,
      aplicarFiltros,
      resetarFiltros,
      filtroBase,
      filtrosVisiveis,
      setFiltrosVisiveis,
      uploadAberto,
      abrirUpload: () => setUploadAberto(true),
      fecharUpload: () => setUploadAberto(false),
      historicoAberto,
      abrirHistorico: () => setHistoricoAberto(true),
      fecharHistorico: () => setHistoricoAberto(false),
      grupos,
      totalEntradas,
      totalSaidas,
      isLoadingEntradas,
      isLoadingSaidas,
      isError,
    }),
    [
      cnpjSelecionado,
      tabAtiva,
      filtroPendente,
      filtroAplicado,
      aplicarFiltros,
      resetarFiltros,
      filtroBase,
      filtrosVisiveis,
      uploadAberto,
      historicoAberto,
      grupos,
      totalEntradas,
      totalSaidas,
      isLoadingEntradas,
      isLoadingSaidas,
      isError,
    ]
  );

  return (
    <GestaoDocumentosContext.Provider value={value}>{children}</GestaoDocumentosContext.Provider>
  );
}

// Re-export UI primitives used across multiple components
export { Table, TableBody, TableCell, TableHead, TableHeader, TableRow };
export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
export { Input };
export { Button };
export { Pagination };
export { SlidersHorizontal };
